import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mantine/core';

import { useSelectAll } from '../../utils/useSelectAll';
import { Button } from '../buttons/Button';
import { Checkbox } from '../checkbox/Checkbox';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Modal } from '../modal/Modal';

import { TableCustomizationSortable } from './TableCustomizationSortable';

import styles from './table-customization.module.scss';

export type TableColumnSettings = {
  id: string;
  label: string;
  sublabel?: string;
  isVisible: boolean;
};

type TableCustomizationValue = {
  fixedColumns: TableColumnSettings[];
  regularColumns: TableColumnSettings[];
};

export type TableCustomizationModalProps = {
  initialValue: TableCustomizationValue;
  modalOpened: boolean;
  onCommit: (newValue: TableCustomizationValue) => void;
  onCancel: () => void;
  onReset: () => void;
  size: number;
  isCustomized: boolean;
  withinPortal?: boolean;
};

export function TableCustomizationModal({
  initialValue,
  modalOpened,
  onCommit,
  onCancel,
  onReset,
  size,
  isCustomized,
  withinPortal = true,
}: TableCustomizationModalProps) {
  const [value, setValue] = useState(initialValue);

  const { t } = useTranslation();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, modalOpened]);

  const handleSelectAllChange = (checked: boolean) => {
    setValue((oldValue) => ({
      ...oldValue,
      fixedColumns: oldValue.fixedColumns.map((c) => ({
        ...c,
        isVisible: checked,
      })),
      regularColumns: oldValue.regularColumns.map((c) => ({
        ...c,
        isVisible: checked,
      })),
    }));
  };

  const { checkboxProps: selectAllCheckboxProps } = useSelectAll(
    value,
    (value) => {
      return value.fixedColumns.every((c) => c.isVisible) &&
        value.regularColumns.every((c) => c.isVisible)
        ? 'all'
        : value.fixedColumns.some((c) => c.isVisible) ||
            value.regularColumns.some((c) => c.isVisible)
          ? 'some'
          : 'none';
    },
    handleSelectAllChange
  );

  return (
    <Modal
      actions={
        <>
          <Button
            icon={<GoogleFontIcon name="close" />}
            onClick={onCancel}
            variant="secondary"
          >
            {t('app.table.customizationModal.cancel')}
          </Button>
          <Button
            icon={<GoogleFontIcon name="check" />}
            onClick={() => {
              onCommit(value);
            }}
          >
            {t('app.table.customizationModal.apply')}
          </Button>
        </>
      }
      footerAction={
        <div className={styles['footer']}>
          <Checkbox
            className={styles['footer__select-all']}
            label={t('app.table.customizationModal.selectAll')}
            data-testid="table-customization-modal__select-all-checkbox"
            {...selectAllCheckboxProps}
          />
          <Button
            className={styles['footer__reset-all']}
            icon={<GoogleFontIcon name="restart_alt" />}
            variant="text"
            onClick={onReset}
            disabled={!isCustomized}
          >
            {t('app.table.customizationModal.resetChanges')}
          </Button>
        </div>
      }
      modalType="action"
      opened={modalOpened}
      onClose={onCancel}
      title={t('app.table.customizationModal.title')}
      withinPortal={withinPortal}
      size={size}
    >
      <div className={styles['columns__main']}>
        <TableCustomizationSortable
          value={value.fixedColumns}
          onChange={(newValue) => {
            setValue((oldValue) => ({
              ...oldValue,
              fixedColumns: newValue,
            }));
          }}
        />
        <Divider
          classNames={{ label: styles['fixed-columns-divider__label'] }}
          label={t('app.table.customizationModal.divider')}
          labelPosition="center"
        />
        <TableCustomizationSortable
          value={value.regularColumns}
          onChange={(newValue) => {
            setValue((oldValue) => ({
              ...oldValue,
              regularColumns: newValue,
            }));
          }}
        />
      </div>
    </Modal>
  );
}
